<template>
    <div id="navbar" ref="navbar" class="top-menu" v-if="shopId" :class="{'-min': coreParams.theme_settings.sidebar && !isMobile}">
        <div>
            <div v-if="!coreParams.theme_settings.sidebar || isMobile" class="top-menu-brand-wrap">
                <router-link :to="i18nLink()" class="top-menu-brand">
                    <img v-if="coreParams.shop_asset_image && coreParams.shop_asset_image.logo_original" v-show="showLogo"
                         :src="coreParams.shop_asset_image.logo_original" :alt="shopId" @load="showLogo = true">
                    <template v-if="coreParams.title && !showLogo">{{ coreParams.title }}</template>
                    <template v-else-if="!showLogo">{{ shopId }}</template>
                </router-link>
            </div>
        </div>
        <ul id="navbar-links" v-if="!isMobile && !coreParams.theme_settings.sidebar" class="top-menu-links">
            <li :id="`navbar-link-${i + 1}`" v-for="(page, i) in pages" :key="i">
                <a href="#" @click.prevent="goTo(page.path)" :class="{active: isActiveRoute(page.path)}">
                    <span class="text-with-icon">{{ page.name }}</span>
                    <span v-if="page.lockIcon" class="material-icon" :class="!!customerAlbums.length ? 'icon-lock_open' : 'icon-lock_outline'"/>
                </a>
            </li>
        </ul>
        <div v-if="!isMobile" id="navbar-actions" class="top-menu-actions">
            <b-dropdown variant="link" v-if="coreParams.multi_language && availableLocales.length > 1" class="top-menu-lang-switcher" right>
                <template slot="button-content">
                    {{ $i18n.locale.toUpperCase() }}
                    <span class="material-icon icon-keyboard_arrow_down"/>
                </template>
                <b-dropdown-item v-for="(lang, i) in langs" :key="i" :active="lang.iso === $i18n.locale" :to="$router.currentRoute.path"
                                 @click.prevent="setLocale(lang.iso)">{{ lang.name || lang.iso.toUpperCase() }}
                </b-dropdown-item>
            </b-dropdown>
            <b-dropdown v-if="favorites && favorites.length > 1" variant="link" class="top-menu-account-menu" id="navbar-favorites" :class="[`${!!favoritesLength ? 'color-dark' : 'color-muted'}`, 'btn', 'btn-link']" right>
                <template slot="button-content">
                    <span class="material-icon icon-library_add"></span>
                    <template v-if="!!favoritesLength">
                        <div class="badge">{{ favoritesLength }}</div>
                    </template>
                </template>
                <b-dropdown-item :to="i18nLink(`selected/${favorite.albumStatus === '2' ? `${favorite.albumHash}-${favorite.albumHashPassword}` : favorite.albumId}`)" v-for="(favorite, i) in favorites" :key="i">
                    {{ favorite.albumTitle || $t('no_name') }}<template v-if="!!favorite.items.length">({{ favorite.items.length }})</template>
                </b-dropdown-item>
            </b-dropdown>
            <span v-else id="navbar-favorites">
                <router-link :to="i18nLink(`selected/${favorites && favorites.length > 0 ? _.first(favorites).albumStatus === '2' ? `${_.first(favorites).albumHash}-${_.first(favorites).albumHashPassword}` : _.first(favorites).albumId : '#'}`)"
                             :class="[`${!!favoritesLength ? 'color-dark' : 'color-muted'}`, 'btn', 'btn-link']">
                    <span>
                        <span class="material-icon icon-library_add"></span>
                    </span>
                    <template v-if="!!favoritesLength">
                        <div class="badge">{{ favoritesLength }}</div>
                    </template>
                </router-link>
            </span>
            <b-tooltip placement="bottom" target="navbar-favorites">
                {{ $t(!!favoritesLength ? 'helpers.my_selection' : 'helpers.my_selection_empty') }}
            </b-tooltip>
            <span id="navbar-cart">
                <router-link :to="i18nLink('cart')"
                             :class="[`${cart && !!cart.quantity ? 'color-dark' : 'color-muted'}`, 'btn', 'btn-link']">
                    <span>
                        <span class="material-icon icon-add_shopping_cart"></span>
                    </span>
                    <template v-if="cart && !!cart.quantity">
                        <div class="badge badge-success">{{ cart.quantity }}</div>
                    </template>
                </router-link>
            </span>
            <b-tooltip placement="bottom" target="navbar-cart">
                {{ $t(cart && !!cart.quantity ? 'helpers.cart' : 'helpers.cart_empty') }}
            </b-tooltip>
            <b-dropdown variant="link" class="top-menu-account-menu" id="navbar-account" right>
                <template slot="button-content">
                    <span class="material-icon icon-account_circle"></span>
                </template>
                <template v-if="user">
                    <b-dropdown-item v-for="(page, i) in accountPages" :key="i" :to="page.path"
                                     :active="isActiveRoute(page.path)">
                        {{ page.name }}
                    </b-dropdown-item>
                    <b-dropdown-item to="#" @click="logout">{{ $t('auth.logout') }}</b-dropdown-item>
                </template>
                <template v-else>
                    <b-dropdown-item :to="i18nLink('login')">{{ $t('auth.login') }}</b-dropdown-item>
                </template>
            </b-dropdown>
            <b-tooltip placement="bottom" target="navbar-account">
                {{ $t('helpers.my_user_account') }}
            </b-tooltip>
        </div>
    </div>
</template>

<style lang="scss">
    @import "../scss/variables";

    $top-menu-height: 100px;

    .top-menu {
        position: fixed;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        height: $top-menu-height;
        padding: 15px 60px;
        top: 0;
        left: 0;
        z-index: 2;
        transition: background-color, opacity .3s ease;
        color: #333;
        background-color: var(--bg-color);

        @media (max-width: 1400px) {
            padding-left: 25px;
            padding-right: 25px;
        }

        @media (max-width: 1200px) {
            padding-left: 20px;
            padding-right: 20px;
        }

        > div {
            display: flex;
            align-items: center;
        }

        &.-min {
            pointer-events: none;
            background-color: transparent;

            > div, > ul{
                opacity: 0;
            }

            .top-menu-actions {
                display: flex;
                width: auto;
                border-radius: 22px;
                opacity: 1;
                pointer-events: auto;
                background-color: var(--bg-color);
                box-shadow: 0 24px 38px 3px rgba(#000, .14), 0 8px 46px 8px rgba(#000, .12), 0 11px 15px -7px rgba(#000, .2);
            }
        }

        .btn {
            padding: 0;
            width: 2.1rem;
            height: 2.1rem;
            line-height: 2.1rem;
        }

        &-toggle {
            margin-right: 40px;
            font-size: 1.6rem;

            @media (max-width: 1200px) {
                margin-right: 15px;
            }

            &,
            &:hover,
            &:focus {
                color: inherit;
            }

            &-overlay {
                position: fixed;
                width: 40px;
                height: 40px;
                top: 28px;
                left: 56px;
                z-index: 2;
                cursor: pointer;

                @media (max-width: 1400px) {
                    left: 22px;
                }

                @media (max-width: 1200px) {
                    left: 11px;
                }
            }
        }

        &-brand {
            font-size: 1.5rem;
            font-weight: 500;

            &-wrap {
                position: relative;
                width: 270px;
                top: -.25rem;
            }

            &,
            &:hover,
            &:focus {
                color: inherit;
                text-decoration: none;
            }

            > img {
                max-width: 220px;
                max-height: 70px;

                @media (max-width: 1200px) {
                    max-width: 200px;
                }
            }
        }

        &-links {
            display: flex;
            align-items: center;
            margin: 0;
            padding: 0;
            list-style: none;

            @media (max-width: 960px) {
                display: none;
            }

            > li {

                > a {
                    display: flex;
                    align-items: center;
                    padding: 10px 20px;
                    height: 50px;
                    font-size: 1rem;
                    border-radius: 25px;
                    text-decoration: none;
                    text-transform: uppercase;
                    transition: color .3s ease;
                    color: var(--text-secondary-color);

                    &.active,
                    &:hover {
                        color: var(--text-color);
                    }

                    &.active {
                        background-color: var(--bg-secondary-color);
                    }

                    .material-icon {
                        margin: 0 .3rem;
                    }
                }
            }
        }

        &-actions {
            width: 270px;
            justify-content: flex-end;
            padding: 2px 12px;

            > * {
                margin-left: 24px;
            }

            .btn {
                position: relative;
                display: flex !important;
                justify-content: center;
                font-size: 1.5rem;
                text-decoration: none !important;

                &,
                &:hover,
                &:focus {
                    color: var(--text-color);
                }

                &.color-muted {
                    color: var(--text-color);
                    opacity: .5;
                }

                &.color-red {

                    &,
                    &:hover,
                    &:focus{
                        color: var(--danger-color);
                    }
                }

                > span {
                    display: flex;
                }

                .badge {
                    position: absolute;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 18px;
                    height: 18px;
                    left: 100%;
                    bottom: 100%;
                    margin: -5px 0 0 -10px;
                    border-radius: 50%;
                    overflow: hidden;
                    color: #232323;
                    font-size: .8rem;
                    background-color: #eee;
                    pointer-events: none;
                    letter-spacing: 0;

                    &-success {
                        color: #fff;
                        background-color: var(--success-color);
                    }
                }

                .material-icon {
                    font-size: 1.87rem !important;
                }
            }

            .color-muted {
                pointer-events: none;
            }
        }

        &--transparent {
            background-color: rgba(#fff, 0);
        }

        &-lang-switcher {
            margin-left: 0;

            > .btn {
                position: relative;
                padding: 0 1.5rem 0 .5rem;
                width: auto;
                font-size: 1rem;
                text-align: left;
                color: var(--text-color);

                &,
                &:hover,
                &:focus {
                    text-decoration: none;
                }

                .material-icon {
                    position: absolute;
                    top: 50%;
                    right: 0;
                    font-size: 1.5rem !important;
                    transform: translateY(-50%);
                }
            }
        }

        &-lang-switcher,
        &-account-menu {
            > .btn::after {
                content: none;
            }
        }

        .btn {
            display: inline-block;
        }
    }
</style>

<script>
  import navbar from 'src/mixins/navbarComponent';

  export default {
    mixins: [navbar],
    mounted() {
      if (!this.isMobile && !this.coreParams.theme_settings.sidebar) {
        window.addEventListener('scroll', () => {
          if (this.$refs.navbar && !this.$refs.navbar.classList.contains('-min') && window.scrollY > 0) {
            this.$refs.navbar.classList.add('-min');
          } else if (this.$refs.navbar && window.scrollY === 0) {
            this.$refs.navbar.classList.remove('-min');
          }
        });
      }
    }
  };
</script>
